import React from 'react';
import PromptSelector from './PromptSelector';
import { BTN_SECONDARY } from '../../lib/buttonStyles';

// Шапка настроек протокола по артборду «Бумаги» (экран «Протокол», состояние
// READY): сетка 96px/1fr, три строки — встреча, объём, модель — и кнопка внизу.
// Всё выбирается чипами: выпадающих списков в макете нет.
//
// 🔴 Шаблон выбирается СПИСКОМ, а не чипами из артборда. В макете их пять и все
// короткие, потому что он рисовался на выдуманных данных; в проде у человека 17
// шаблонов с именами до 41 знака, и рядом плашек из них не выйдет. Разбор — в
// шапке PromptSelector.

const LABEL = 'font-mono text-[11px] uppercase tracking-[0.08em] text-paper-ink-3';

function Chip({ active, children, ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      className={`rounded-chip px-[10px] py-[5px] text-[13px] font-medium transition-colors ${
        active ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink hover:bg-paper-panel'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Запуск сборки: «Сформировать» / «Собрать заново», прогресс и «Отменить».
 * На десктопе стоит справа в шапке настроек, на телефоне — липкой строкой
 * внизу экрана (мобильный макет «Бумаги»: основной призыв над краем экрана).
 */
export function ProtocolActions({
  isProcessing, taskStatus, progress, onCancel, hasResult = false, onNewProtocol,
  onProcess, selectedPromptId, mobile = false,
}) {
  const big = mobile ? 'h-[52px] rounded-[14px] text-[15px]' : 'rounded-control text-[14px]';
  if (isProcessing) {
    return (
      <div className={`flex items-center ${mobile ? 'gap-2' : 'gap-[14px]'}`}>
        <button type="button" disabled
                className={`${big} ${mobile ? 'flex-1' : ''} bg-paper-bg text-paper-hint px-5 py-[10px] font-semibold`}>
          {taskStatus === 'pending' ? 'В очереди…' : `${progress}%`}
        </button>
        <button type="button" onClick={onCancel}
                className={mobile ? 'h-[52px] rounded-[14px] bg-paper-card px-5 text-[15px] font-medium text-paper-ink' : BTN_SECONDARY}>
          Отменить
        </button>
      </div>
    );
  }
  return (
    <div className={`flex items-center ${mobile ? 'gap-2' : 'gap-[14px]'}`}>
      {/* «Новый протокол» приходит и уходит, но место у него постоянное —
          слева от кнопки. Сама кнопка не двигается никогда, меняется
          только подпись: собранный документ пересобирается ею же. */}
      {hasResult && onNewProtocol && (
        <button type="button" onClick={onNewProtocol}
                className={mobile ? 'h-[52px] rounded-[14px] bg-paper-card px-4 text-[15px] font-medium text-paper-ink' : BTN_SECONDARY}>
          {mobile ? 'Новый' : 'Новый протокол'}
        </button>
      )}
      <button
        type="button"
        onClick={onProcess}
        disabled={!selectedPromptId}
        title={!selectedPromptId ? 'Выберите шаблон' : undefined}
        className={`${big} ${mobile ? 'flex-1' : ''} bg-paper-accent hover:bg-paper-accent-hover text-white px-6 py-[12px] font-semibold whitespace-nowrap disabled:bg-paper-bg disabled:text-paper-hint transition-colors`}
      >
        {hasResult ? 'Собрать заново' : 'Сформировать протокол'}
      </button>
    </div>
  );
}

export default function SettingsBar({
  prompts,
  selectedPromptId,
  defaultPromptId,
  isProcessing,
  taskStatus,
  progress,
  levels = [],
  detailLevel = 0,
  onSelectLevel,
  onSelectPrompt,
  onClonePrompt,
  onCreatePrompt,
  onProcess,
  onCancel,
  hasResult = false,
  onNewProtocol,
  onSetDefault,
  onEditPrompt,
  onDeletePrompt,
  // Телефон: шапка свёрнута в одну строку «шаблон · объём» и раскрывается по
  // нажатию, запуск — внизу экрана (ProtocolActions). Развёрнутой она занимала
  // весь экран, и ни расшифровки, ни протокола, ни чата видно не было
  // (тест владельца на iPhone, 27.09.2026).
  mobile = false,
  expanded = false,
  onToggle,
}) {
  const templateRow = (
    <div className="flex items-center gap-3 min-w-0">
      <span className={LABEL}>Шаблон</span>
      <PromptSelector
        prompts={prompts}
        selectedId={selectedPromptId}
        defaultPromptId={defaultPromptId}
        onSelect={onSelectPrompt}
        onClone={onClonePrompt}
        onCreate={onCreatePrompt}
        onSetDefault={onSetDefault}
        onEdit={onEditPrompt}
        onDelete={onDeletePrompt}
      />
    </div>
  );
  const levelRow = (
    <div className={`flex ${mobile ? 'items-start' : 'items-center'} gap-3`}>
      <span className={`${LABEL} ${mobile ? 'pt-[6px]' : ''}`}>Объём</span>
      <div className="flex flex-wrap items-center gap-1">
        {levels.map((l, i) => (
          <Chip key={l.label} active={i === detailLevel} disabled={isProcessing}
                title={l.hint}
                onClick={() => onSelectLevel && onSelectLevel(i)}>
            {l.label}
          </Chip>
        ))}
      </div>
    </div>
  );

  if (mobile) {
    const selected = (prompts || []).find((p) => p._id === selectedPromptId);
    return (
      <div className="kbd-hide bg-paper-card rounded-card shrink-0 text-[13px]">
        <button type="button" onClick={onToggle} aria-expanded={expanded}
                data-testid="protocol-settings-toggle"
                className="w-full min-h-12 px-4 flex items-center gap-2 text-left">
          <span className="flex-1 min-w-0 truncate">
            <span className="font-semibold text-paper-ink">{selected ? selected.name : 'Выберите шаблон'}</span>
            <span className="text-paper-ink-3"> · {levels[detailLevel]?.label}</span>
          </span>
          <span aria-hidden="true" className="text-paper-ink-3">{expanded ? '▴' : '▾'}</span>
        </button>
        {expanded && (
          <div className="px-4 pb-4 flex flex-col gap-3">
            {templateRow}
            {levelRow}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-paper-card rounded-card px-[22px] py-[14px] flex items-center justify-between gap-6 text-[13px] shrink-0">
      {/* Компоновка по решению владельца 07.09.2026: слева два ряда настроек —
          шаблон с моделью в одну строку, объём под шаблоном, — справа запуск.
          В артборде метка и контрол шли колонкой, и шапка занимала больше места,
          чем сам документ. Описание шаблона отсюда убрано: оно жило подсказкой
          рядом с кнопкой и мешало ей, а в списке шаблонов остаётся. */}
      <div className="flex flex-col gap-[10px] min-w-0">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {templateRow}

          {/* 🔴 Выбора модели здесь больше нет. Кнопка «В облаке» отправляла
              текст расшифровки внешней модели за пределы России, а согласие на
              обработку персональных данных обещает обработку строго на
              территории РФ — интерфейс опровергал наш же документ. Решение
              владельца 21.09.2026: убрать у всех. Запасная модель на случай
              перегрузки выбирается сервисом, а не человеком, и задаётся
              настройкой `cloud_llm_model` в админке. */}
        </div>

        {levelRow}
      </div>

      <div className="shrink-0">
        <ProtocolActions isProcessing={isProcessing} taskStatus={taskStatus} progress={progress}
                         onCancel={onCancel} hasResult={hasResult} onNewProtocol={onNewProtocol}
                         onProcess={onProcess} selectedPromptId={selectedPromptId} />
      </div>
    </div>
  );
}
